import React, { useContext } from "react";
import { Badge, Box, Divider, VStack, HStack, Text, Button, Icon, useBreakpointValue, Spinner } from "@chakra-ui/react";
import {
    MdAdminPanelSettings,
    MdSupportAgent,
    MdInsights,
    MdAccountBalance,
    MdGavel,
    MdVisibility,
    MdScience,
    MdHome,
    MdPeople,
    MdPsychology,
    MdScoreboard,
    MdBook,
    MdPhoneInTalk,
    MdFlag,
    MdBarChart,
    MdSegment,
    MdStorage,
    MdSettings,
    MdMoney,
    MdDomain,
    MdImportExport,
    MdCode,
    MdLink,
} from "react-icons/md";
import { planForBillingTier, workspacePlanLabel } from "../config/plans";
import { Link, useLocation } from "react-router-dom";
import AuthContext from "../context/AuthContext";
import Loading from "./Loading";

const dashboardRoleItems = [
    { name: "Demo Workspace", icon: MdScience, path: "/dashboard/demo", dashboardRole: "demo" },
    { name: "Admin", icon: MdAdminPanelSettings, path: "/dashboard/admin?view=today", dashboardRole: "admin" },
    { name: "Relationship Manager", icon: MdSupportAgent, path: "/dashboard/rm?view=today", dashboardRole: "rm" },
    { name: "Head of Advisory", icon: MdInsights, path: "/dashboard/head?view=today", dashboardRole: "head" },
    { name: "Portfolio Adviser", icon: MdAccountBalance, path: "/dashboard/portfolio?view=today", dashboardRole: "portfolio" },
    { name: "Compliance", icon: MdGavel, path: "/dashboard/compliance?view=today", dashboardRole: "compliance" },
    { name: "View Only", icon: MdVisibility, path: "/dashboard/view-only?view=today", dashboardRole: "view_only" },
];

const workspaceViewsByRole = {
    admin: [
        { name: "Today", icon: MdHome, path: "/dashboard/admin?view=today", view: "today" },
        { name: "Customer base", icon: MdPeople, path: "/dashboard/admin?view=clients", view: "clients" },
        { name: "Individual scores", icon: MdScoreboard, path: "/dashboard/admin?view=scores", view: "scores" },
        { name: "Bias benchmark", icon: MdPsychology, path: "/dashboard/admin?view=bias", view: "bias" },
        { name: "Platform impact", icon: MdBarChart, path: "/dashboard/admin?view=impact", view: "impact" },
    ],
    rm: [
        { name: "Today", icon: MdHome, path: "/dashboard/rm?view=today", view: "today" },
        { name: "Customer base", icon: MdPeople, path: "/dashboard/rm?view=clients", view: "clients" },
        { name: "Own book", icon: MdBook, path: "/dashboard/rm?view=own-book", view: "own-book" },
        { name: "Who to call", icon: MdPhoneInTalk, path: "/dashboard/rm?view=who-to-call", view: "who-to-call" },
        { name: "Priority scores", icon: MdFlag, path: "/dashboard/rm?view=priority", view: "priority" },
        { name: "Risk ranking", icon: MdBarChart, path: "/dashboard/rm?view=risk", view: "risk" },
        { name: "Segments", icon: MdSegment, path: "/dashboard/rm?view=segments", view: "segments" },
    ],
    head: [
        { name: "Today", icon: MdHome, path: "/dashboard/head?view=today", view: "today" },
        { name: "Adoption", icon: MdPeople, path: "/dashboard/head?view=adoption", view: "adoption" },
        { name: "Conversion", icon: MdFlag, path: "/dashboard/head?view=conversion", view: "conversion" },
        { name: "Risk trend", icon: MdBarChart, path: "/dashboard/head?view=risk", view: "risk" },
        { name: "Head dashboard", icon: MdInsights, path: "/dashboard/head?view=dashboard", view: "dashboard" },
        { name: "Bias benchmark", icon: MdPsychology, path: "/dashboard/head?view=bias", view: "bias" },
    ],
    portfolio: [
        { name: "Today", icon: MdHome, path: "/dashboard/portfolio?view=today", view: "today" },
        { name: "Mismatch", icon: MdFlag, path: "/dashboard/portfolio?view=mismatch", view: "mismatch" },
        { name: "Allocation drift", icon: MdBarChart, path: "/dashboard/portfolio?view=drift", view: "drift" },
        { name: "Evidence", icon: MdBook, path: "/dashboard/portfolio?view=evidence", view: "evidence" },
        { name: "Portfolio dashboard", icon: MdAccountBalance, path: "/dashboard/portfolio?view=dashboard", view: "dashboard" },
        { name: "Customer base", icon: MdPeople, path: "/dashboard/portfolio?view=clients", view: "clients" },
        { name: "Bias benchmark", icon: MdPsychology, path: "/dashboard/portfolio?view=bias", view: "bias" },
    ],
    compliance: [
        { name: "Today", icon: MdHome, path: "/dashboard/compliance?view=today", view: "today" },
        { name: "SLA queue", icon: MdFlag, path: "/dashboard/compliance?view=sla", view: "sla" },
        { name: "Evidence", icon: MdBook, path: "/dashboard/compliance?view=evidence", view: "evidence" },
        { name: "Audit pack", icon: MdImportExport, path: "/dashboard/compliance?view=audit-pack", view: "audit-pack" },
        { name: "Compliance dashboard", icon: MdGavel, path: "/dashboard/compliance?view=dashboard", view: "dashboard" },
        { name: "Individual scores", icon: MdScoreboard, path: "/dashboard/compliance?view=scores", view: "scores" },
        { name: "Bias benchmark", icon: MdPsychology, path: "/dashboard/compliance?view=bias", view: "bias" },
        { name: "Audit", icon: MdGavel, path: "/auditsCompliance", view: "audit" },
    ],
    view_only: [
        { name: "Today", icon: MdHome, path: "/dashboard/view-only?view=today", view: "today" },
        { name: "Bias benchmark", icon: MdPsychology, path: "/dashboard/view-only?view=bias", view: "bias" },
    ],
};

const operationItems = [
    { name: "Datasets", icon: MdStorage, path: "/datasets", visible: (user) => user?.role === "admin" },
    { name: "Connected pilot", icon: MdLink, path: "/connected", visible: (user) => user?.role === "admin" && user?.subscription === "business" },
    { name: "Settings", icon: MdSettings, path: "/settings", visible: (user) => user?.role === "admin" },
    { name: "Reports & Exports", icon: MdImportExport, path: "/reportsExports", visible: (user) => ["admin", "rm"].includes(user?.role) && ["solo", "pro", "business"].includes(user?.subscription) },
];

const administrationItems = [
    { name: "Subscription", icon: MdMoney, path: "/subscription", visible: (user) => user?.role === "admin" },
    { name: "Your Domain", icon: MdDomain, path: "/domain", visible: (user) => user?.role === "admin" && user?.subscription === "business" },
    { name: "Dev Portal", icon: MdCode, path: "/docs", visible: (user) => user?.role === "admin" && user?.subscription === "business" },
    { name: "Audits", icon: MdGavel, path: "/auditsCompliance", visible: (user) => user?.subscription === "business" && ["admin", "compliance"].includes(user?.role) },
];

const canShowDashboardItem = (user, dashboardRole) => {
    if (!dashboardRole) return true;
    if (dashboardRole === "demo") {
        // Demo workspace stays available on paid plans for buyer demos.
        return true;
    }

    if (dashboardRole === "view_only") {
        return user?.subscription === "business" && ["admin", "view_only"].includes(user?.role);
    }

    if (user?.role === "admin") {
        if (user?.subscription === "business") {
            return ["admin", "rm", "head", "portfolio", "compliance", "view_only"].includes(dashboardRole);
        }
        // Solo: the admin is also the adviser (the RM workspace shows the whole book).
        if (["solo", "pro"].includes(user?.subscription)) return ["admin", "rm"].includes(dashboardRole);
        return false;
    }

    const normalizedUserRole = user?.role === "portfolio_adviser" ? "portfolio" : user?.role;
    return normalizedUserRole === dashboardRole;
};

const dashboardRoleFromPath = (pathname, user) => {
    if (pathname.startsWith("/dashboard/admin")) return "admin";
    if (pathname.startsWith("/dashboard/rm")) return "rm";
    if (pathname.startsWith("/dashboard/head")) return "head";
    if (pathname.startsWith("/dashboard/portfolio")) return "portfolio";
    if (pathname.startsWith("/dashboard/compliance")) return "compliance";
    if (pathname.startsWith("/dashboard/view-only")) return "view_only";
    if (pathname.startsWith("/dashboard/demo")) return "demo";
    if (user?.role === "portfolio_adviser") return "portfolio";
    return user?.role || "admin";
};

const getCurrentView = (search) => new URLSearchParams(search).get("view") || "today";

const getNavSections = (user, location) => {
    const activeRole = dashboardRoleFromPath(location.pathname, user);
    const roleItems = dashboardRoleItems
        .filter(item => canShowDashboardItem(user, item.dashboardRole))
        .map(item => (user?.subscription === "solo" && item.dashboardRole === "rm" ? { ...item, name: "My reviews" } : item));
    const workspaceItems = (workspaceViewsByRole[activeRole] || []).filter((item) => {
        if (item.view === "audit") return user?.subscription === "business" && ["admin", "compliance"].includes(user?.role);
        // Platform impact measures advisers' coverage: a team view.
        if (item.view === "impact") return ["pro", "business"].includes(user?.subscription);
        return activeRole !== "demo";
    });

    return [
        {
            title: user?.subscription === "free" ? "Playground" : "Role Workspaces",
            items: roleItems,
            type: "role",
        },
        {
            title: "Workspace Views",
            items: workspaceItems,
            type: "view",
        },
        {
            title: "Operations",
            items: operationItems.filter(item => item.visible(user)),
            type: "link",
        },
        {
            title: "Administration",
            items: administrationItems.filter(item => item.visible(user)),
            type: "link",
        },
    ].filter(section => section.items.length > 0);
};

const stripSearch = (path) => path.split("?")[0];
const itemSearchView = (path) => new URLSearchParams(path.split("?")[1] || "").get("view") || "today";

const isItemActive = (location, item, sectionType, user) => {
    const itemPath = stripSearch(item.path);
    if (sectionType === "role") {
        return dashboardRoleFromPath(location.pathname, user) === item.dashboardRole;
    }
    if (sectionType === "view") {
        if (location.pathname !== itemPath) return false;
        return getCurrentView(location.search) === itemSearchView(item.path);
    }
    return location.pathname === itemPath || (itemPath !== "/dashboard" && location.pathname.startsWith(`${itemPath}/`));
};

function SideBar() {
    const { token, user, isAuthInitializing } = useContext(AuthContext);
    const location = useLocation();
    const isMobile = useBreakpointValue({ base: true, md: false });

    if (isAuthInitializing || !token) {
        return (
            <Loading />
        );
    }

    if (!user) {
        return (
            <Box minH="100vh" display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                <Spinner size="xl" color="brand.teal" />
            </Box>
        );
    }

    if (isMobile) {
        const filteredNavItems = getNavSections(user, location).flatMap(section => section.items.map(item => ({ ...item, sectionType: section.type })));
        // Mobile navbar layout
        return (
            <Box
                position="fixed"
                bottom={0}
                left={0}
                right={0}
                bg="brand.sidebar"
                p={2}
                borderTop="1px solid"
                borderColor="brand.divider"
                zIndex={1000}
                overflowX="auto"
            >
                <HStack justify="flex-start" spacing={1} minW="max-content">
                    {filteredNavItems.map((item) => {
                        const isActive = isItemActive(location, item, item.sectionType, user);
                        return (
                            <Button
                                as={Link}
                                to={item.path}
                                key={item.name}
                                variant="ghost"
                                bg={isActive ? "brand.tealSoft" : "transparent"}
                                color={isActive ? "brand.navy" : "brand.muted"}
                                _hover={{
                                    bg: isActive ? "brand.tealSoft" : "brand.sidebarHover",
                                    color: "brand.navy",
                                }}
                                borderRadius="md"
                                fontWeight={isActive ? "bold" : "medium"}
                                size="sm"
                                flex="0 0 auto"
                                w="86px"
                                height="60px"
                                display="flex"
                                flexDirection="column"
                                gap={1}
                            >
                                <Icon as={item.icon} boxSize={5} />
                                <Text fontSize="xs" fontWeight="normal">
                                    {item.name}
                                </Text>
                            </Button>
                        );
                    })}
                </HStack>
            </Box>
        );
    }

    const navSections = getNavSections(user, location);
    // Desktop sidebar layout (original)
    return (
        <Box
            w="244px"
            h="calc(100vh - 56px)"
            bg="brand.sidebar"
            p={3}
            display="flex"
            flexDirection="column"
            borderRight="1px solid"
            borderColor="brand.divider"
            position="sticky"
            top="56px"
            alignSelf="flex-start"
            flexShrink={0}
            overflowY="auto"
            zIndex={1}
        >
            <VStack align="stretch" spacing={5} flex="1">
                {navSections.map((section, sectionIndex) => (
                    <Box key={section.title}>
                        {sectionIndex > 0 ? <Divider borderColor="brand.divider" mb={4} /> : null}
                        <Text
                            fontSize="10px"
                            fontWeight="800"
                            letterSpacing="0.12em"
                            textTransform="uppercase"
                            color="brand.subtle"
                            px={3}
                            mb={2}
                        >
                            {section.title}
                        </Text>
                        <VStack align="stretch" spacing={1}>
                            {section.items.map((item) => {
                                const isActive = isItemActive(location, item, section.type, user);
                                return (
                                    <Button
                                        as={Link}
                                        to={item.path}
                                        key={item.name}
                                        justifyContent="flex-start"
                                        leftIcon={<Icon as={item.icon} boxSize={4} color={isActive ? "brand.tealDark" : "brand.subtle"} />}
                                        variant="ghost"
                                        bg={isActive ? "brand.tealSoft" : "transparent"}
                                        color={isActive ? "brand.navy" : "brand.muted"}
                                        _hover={{
                                            bg: isActive ? "brand.tealSoft" : "brand.sidebarHover",
                                            color: "brand.navy",
                                        }}
                                        borderRadius="8px"
                                        fontWeight={isActive ? "800" : "600"}
                                        h="38px"
                                        fontSize="13px"
                                        position="relative"
                                        overflow="hidden"
                                    >
                                        {isActive ? (
                                            <Box position="absolute" left={0} top="7px" bottom="7px" w="3px" bg="brand.teal" borderRadius="2px" />
                                        ) : null}
                                        <Text isTruncated>{item.name}</Text>
                                    </Button>
                                );
                            })}
                        </VStack>
                    </Box>
                ))}
            </VStack>
            <Box borderTop="1px solid" borderColor="brand.divider" pt={3} mt={5} px={2}>
                <HStack spacing={2} mb={2}>
                    <Box w="6px" h="6px" borderRadius="full" bg="brand.teal" />
                    <Text fontSize="11px" color="brand.subtle">
                        {user?.noOfFileUploads > 0 ? 'Tenant data · scored' : 'No tenant data yet'}
                    </Text>
                </HStack>
                <HStack bg="brand.tealSoft" borderRadius="8px" px={2.5} py={2} spacing={2}>
                    <Icon as={MdScience} color="brand.tealDark" boxSize={4} />
                    <Text fontSize="11.5px" fontWeight="700" color="brand.tealDark" flex="1">
                        {workspacePlanLabel(user) ? `${workspacePlanLabel(user)} workspace` : user?.readOnlyUntil ? "Plan ended · export only" : "Demo mode"}
                    </Text>
                    <Badge bg="white" color="brand.tealDark">{planForBillingTier(user?.subscription)?.name || user?.subscription || "free"}</Badge>
                </HStack>
            </Box>
        </Box>
    );
}

export default SideBar;
